import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Code,
  CopyButton,
  Group,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconAlertCircle, IconCheck, IconCopy, IconDeviceFloppy, IconSend, IconX } from '@tabler/icons-react';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TemplateValidationResult } from '../../lib/template-validation';

export interface AutoResponseProps {
  // Display data (doesn't change on typing)
  availableVariables: Array<{ name: string; description: string }>;
  templateExample: string;
  hasEmailField: boolean;
  hasFormSpec: boolean;

  // Validation (controlled by parent)
  subjectValidation: TemplateValidationResult;
  textValidation: TemplateValidationResult;
  onValidateSubject: (should: boolean) => void;
  onValidateText: (should: boolean) => void;

  // Form state
  formValues: {
    check_challenge: boolean;
    check_specs: boolean;
    auto_response_enabled: boolean;
    auto_response_subject: string | undefined;
    auto_response_text: string | undefined;
  };
  onFormChange: (field: string, value: any) => void;

  // Other props
  hasEmailRecipients: boolean;
  isBusy: boolean;
  hasError: {
    title: string;
    message: string;
  } | null;
  submitCb: () => Promise<void>;
  hasUnsavedChanges: boolean;
  onCancel: () => void;
  // Reset trigger - increment to reset local state
  resetKey?: number;
  onSendTest?: (email: string) => Promise<void>;
  userEmail?: string;
}

function SendTestSection({ onSendTest, userEmail, hasUnsavedChanges }: { onSendTest: (email: string) => Promise<void>; userEmail?: string; hasUnsavedChanges?: boolean }) {
  const { t } = useTranslation();
  const [testEmail, setTestEmail] = useState(userEmail || '');
  const [sending, setSending] = useState(false);

  return (
    <Card withBorder mt="md" p="sm">
      <Title order={5} size="sm" mb="xs">
        {t('autoResponse.sendTest')}
      </Title>
      <Group align="flex-end">
        <TextInput
          label={t('autoResponse.testEmailLabel')}
          placeholder="you@example.com"
          value={testEmail}
          onChange={(e) => setTestEmail(e.currentTarget.value)}
          style={{ flex: 1 }}
        />
        <Button
          leftSection={<IconSend size={16} />}
          loading={sending}
          disabled={!testEmail || hasUnsavedChanges}
          onClick={async () => {
            setSending(true);
            try {
              await onSendTest(testEmail);
              notifications.show({
                title: t('autoResponse.testSent'),
                message: testEmail,
                color: 'green',
              });
            } catch (err: any) {
              console.error('Auto-response test failed:', err);
              notifications.show({
                title: t('autoResponse.testFailed'),
                message: err?.response?.data?.message || err?.message || testEmail,
                color: 'red',
              });
            } finally {
              setSending(false);
            }
          }}
        >
          {t('autoResponse.sendTest')}
        </Button>
      </Group>
    </Card>
  );
}

// Make it a pure component with React.memo
export const AutoResponse = React.memo((props: AutoResponseProps) => {
  // Local state for instant typing response
  const [localEnabled, setLocalEnabled] = useState(props.formValues.auto_response_enabled);
  const [localSubject, setLocalSubject] = useState(props.formValues.auto_response_subject || '');
  const [localText, setLocalText] = useState(props.formValues.auto_response_text || '');

  // Debounced values - sync to parent after 500ms
  const [debouncedSubject] = useDebouncedValue(localSubject, 500);
  const [debouncedText] = useDebouncedValue(localText, 500);

  // Reset local state when resetKey changes (cancel operation)
  useEffect(() => {
    setLocalEnabled(props.formValues.auto_response_enabled);
    setLocalSubject(props.formValues.auto_response_subject || '');
    setLocalText(props.formValues.auto_response_text || '');
  }, [props.resetKey]);

  // Sync debounced values to parent form
  // Normalize empty strings to undefined to match originalValuesRef convention in Edit.tsx
  useEffect(() => {
    const normalized = debouncedSubject || undefined;
    if (normalized !== (props.formValues.auto_response_subject || undefined)) {
      props.onFormChange('auto_response_subject', normalized);
    }
  }, [debouncedSubject]);

  useEffect(() => {
    const normalized = debouncedText || undefined;
    if (normalized !== (props.formValues.auto_response_text || undefined)) {
      props.onFormChange('auto_response_text', normalized);
    }
  }, [debouncedText]);

  return (
    <>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          props.submitCb();
        }}
      >
        <Stack gap="xs">
          {!props.formValues.check_challenge && (
            <Alert color="primary" icon={<IconAlertCircle size={20} />}>
              Auto-responses are only sent when captcha is enabled and the submission passes
              verification.
            </Alert>
          )}

          {!props.hasEmailRecipients && (
            <Alert color="primary" icon={<IconAlertCircle size={20} />}>
              At least one email recipient is needed as the reply-to address for auto-responses.
            </Alert>
          )}

          {props.formValues.check_specs && props.hasFormSpec && !props.hasEmailField && (
            <Alert color="orange" icon={<IconAlertCircle size={20} />}>
              Your form specification doesn't include an email field. Auto-responses need an
              email field to know where to send the reply.
            </Alert>
          )}

          {!props.formValues.check_specs && (
            <Alert color="primary" icon={<IconAlertCircle size={20} />}>
              Make sure your form includes an <Code>email</Code> field so auto-responses can be sent
              to the right address.
            </Alert>
          )}

          <Checkbox
            label="Enable Auto-Response"
            description="Send automated responses to form submitters"
            checked={localEnabled}
            disabled={!props.hasEmailRecipients || !props.formValues.check_challenge}
            onChange={(event) => {
              setLocalEnabled(event.currentTarget.checked);
              props.onFormChange('auto_response_enabled', event.currentTarget.checked);
            }}
          />

          {localEnabled && (
            <Box>
              <TextInput
                label="Auto-Response Email Subject"
                placeholder={`Thank you for your message - {{ name | "Valued Customer" }}`}
                value={localSubject}
                onChange={(event) => setLocalSubject(event.currentTarget.value)}
                onFocus={() => props.onValidateSubject(false)}
                onBlur={() => props.onValidateSubject(true)}
                description="Subject line for the auto-response email (supports template variables)"
                required
                mb="md"
              />

              {/* Title template validation alerts */}
              {localSubject && props.subjectValidation.errors.length > 0 && (
                <Alert icon={<IconAlertCircle size={16} />} color="red" mt="xs" mb="md">
                  <Text size="sm" fw={500} mb="xs">
                    Subject Template Syntax Errors:
                  </Text>
                  {props.subjectValidation.errors.map((error, index) => (
                    <Text key={index} size="xs">
                      • {error}
                    </Text>
                  ))}
                </Alert>
              )}

              {localSubject &&
                props.subjectValidation.warnings.length > 0 &&
                props.subjectValidation.errors.length === 0 && (
                  <Alert icon={<IconAlertCircle size={16} />} color="yellow" mt="xs" mb="md">
                    <Text size="sm" fw={500} mb="xs">
                      Subject Template Suggestions:
                    </Text>
                    {props.subjectValidation.warnings.map((warning, index) => (
                      <Text key={index} size="xs">
                        • {warning}
                      </Text>
                    ))}
                  </Alert>
                )}

              <Textarea
                label="Auto-Response Message"
                placeholder={`Hello {{ name | "there" }},

Thank you for contacting us! We've received your message and appreciate you reaching out.

If you have any urgent questions, please don't hesitate to call us directly.

Best regards,
The Support Team`}
                value={localText}
                onChange={(event) => setLocalText(event.currentTarget.value)}
                onFocus={() => props.onValidateText(false)}
                onBlur={() => props.onValidateText(true)}
                rows={8}
                description="Message sent to form submitters (supports template variables)"
              />

              {/* Template validation alerts */}
              {localText && props.textValidation.errors.length > 0 && (
                <Alert icon={<IconAlertCircle size={16} />} color="red" mt="xs">
                  <Text size="sm" fw={500} mb="xs">
                    Message Template Syntax Errors:
                  </Text>
                  {props.textValidation.errors.map((error, index) => (
                    <Text key={index} size="xs">
                      • {error}
                    </Text>
                  ))}
                </Alert>
              )}

              {localText &&
                props.textValidation.warnings.length > 0 &&
                props.textValidation.errors.length === 0 && (
                  <Alert icon={<IconAlertCircle size={16} />} color="yellow" mt="xs">
                    <Text size="sm" fw={500} mb="xs">
                      Message Template Suggestions:
                    </Text>
                    {props.textValidation.warnings.map((warning, index) => (
                      <Text key={index} size="xs">
                        • {warning}
                      </Text>
                    ))}
                  </Alert>
                )}

              <Card withBorder mt="md" p="sm">
                <Title order={5} size="sm" mb="xs">
                  Template Variables
                </Title>
                <Text size="xs" mb="sm" c="dimmed">
                  Use these variables in your message. Provide fallback text after the | symbol.
                </Text>

                {props.hasFormSpec ? (
                  <Alert icon={<IconAlertCircle size={14} />} color="blue" mb="sm">
                    <Text size="xs">
                      These variables are based on your form fields. Each field in your form
                      specification can be used as a template variable.
                    </Text>
                  </Alert>
                ) : (
                  <Alert icon={<IconAlertCircle size={14} />} color="yellow" mb="sm">
                    <Text size="xs">
                      These are example variables. Define form fields in the "Form Fields" section
                      to see your actual available variables here.
                    </Text>
                  </Alert>
                )}

                <Stack gap="xs">
                  {props.availableVariables.map((variable) => (
                    <Group key={variable.name} gap="xs">
                      <Code>{`{{ ${variable.name} | "fallback" }}`}</Code>
                      <Text size="xs" c="dimmed">
                        {variable.description}
                      </Text>
                    </Group>
                  ))}
                </Stack>

                <Title order={6} size="xs" mt="md" mb="xs">
                  Example Subjects
                </Title>
                <Stack gap={4}>
                  {[
                    `Thank you for your message, {{ name | "Valued Customer" }}`,
                    `Re: {{ subject | "Your inquiry" }} — confirmation`,
                    `{{ company | "Our Team" }} — we received your submission`,
                  ].map((subject) => (
                    <Group key={subject} gap="xs" wrap="nowrap">
                      <Code style={{ flex: 1, fontSize: '11px', backgroundColor: 'var(--mantine-color-default-hover)' }}>{subject}</Code>
                      <CopyButton value={subject}>
                        {({ copied, copy }) => (
                          <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow>
                            <ActionIcon size="xs" variant="subtle" color={copied ? 'teal' : 'gray'} onClick={copy}>
                              {copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </CopyButton>
                    </Group>
                  ))}
                </Stack>

                <Title order={6} size="xs" mt="md" mb="xs">
                  Example Message
                </Title>
                <Code block style={{ fontSize: '11px', whiteSpace: 'pre-wrap' }}>{props.templateExample}</Code>
              </Card>

              {props.onSendTest && (
                <SendTestSection onSendTest={props.onSendTest} userEmail={props.userEmail} hasUnsavedChanges={props.hasUnsavedChanges} />
              )}

              {localText && localText.length > 1800 && (
                <Alert icon={<IconAlertCircle size={16} />} color="yellow" mt="xs">
                  Message is getting long ({localText.length}/2000 characters). Consider keeping it
                  concise for better delivery.
                </Alert>
              )}
            </Box>
          )}

          {props.hasError && (
            <Alert mt="md" icon={<IconAlertCircle size={16} />} title={props.hasError.title} color="red">
              {props.hasError.message}
            </Alert>
          )}

          {props.hasUnsavedChanges && (
            <Group justify="flex-end">
              <Button variant="outline" onClick={props.onCancel} leftSection={<IconX size={16} />}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={props.isBusy}
                leftSection={<IconDeviceFloppy size={16} />}
              >
                Save Changes
              </Button>
            </Group>
          )}
        </Stack>
      </form>
    </>
  );
}, (prevProps, nextProps) => {
  // Custom comparison - only re-render when display-affecting props change
  // Local state handles text input, so we don't need to re-render for every keystroke
  return (
    // Display data (recomputed only when form spec changes)
    prevProps.availableVariables === nextProps.availableVariables &&
    prevProps.templateExample === nextProps.templateExample &&
    prevProps.hasEmailField === nextProps.hasEmailField &&
    prevProps.hasFormSpec === nextProps.hasFormSpec &&
    // Validation results (only change on blur)
    prevProps.subjectValidation === nextProps.subjectValidation &&
    prevProps.textValidation === nextProps.textValidation &&
    // Form state (checkbox, etc)
    prevProps.formValues.check_challenge === nextProps.formValues.check_challenge &&
    prevProps.formValues.check_specs === nextProps.formValues.check_specs &&
    prevProps.formValues.auto_response_enabled === nextProps.formValues.auto_response_enabled &&
    // Other state
    prevProps.hasEmailRecipients === nextProps.hasEmailRecipients &&
    prevProps.isBusy === nextProps.isBusy &&
    prevProps.hasUnsavedChanges === nextProps.hasUnsavedChanges &&
    prevProps.hasError === nextProps.hasError &&
    // Reset trigger - must re-render so useEffect can fire
    prevProps.resetKey === nextProps.resetKey
    // NOTE: We intentionally exclude formValues.auto_response_subject/text
    // because local state handles those - no need to re-render parent component
  );
});
